import { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SyncIcon from "@mui/icons-material/Sync";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useMutation, useQuery } from "react-query";
import DialogWrapper from "components/DialogWrapper";
import BillingEntityOption from "components/BillingEntityOption";
import { snack } from "components/toast";
import { handleError } from "utils/handleError";
import { ResType } from "types";
import {
  getTallyBillingEntities,
  getTallySyncableInvoices,
  saveTallyCompanyMapping,
} from "api/services/billing/tallySync";
import {
  getCompanionStatus,
  getOpenCompanies,
  getStoredBridgeToken,
  pushInvoicesToTally,
  setStoredBridgeToken,
} from "api/services/billing/tallyCompanion";

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type BridgeState = "checking" | "connected" | "not_found";

// Pilot-stage hosting: uploaded directly to the S3 bucket ATOM already
// uses for file storage, at a public path. Revisit once this goes past a
// handful of pilot users (versioning, a proper "Bridge" landing page).
const CONSOLE_DOWNLOAD_URL = "https://vider-prod-bucket.s3.ap-south-1.amazonaws.com/atom-tally-bridge.exe";
// Keep this matching whatever build is actually live at CONSOLE_DOWNLOAD_URL
// -- atom_tally_bridge.py's own /api/status reports "0.1.0-demo" internally, this
// label just reads better without "-demo" for something pilot users see.
const BRIDGE_VERSION = "v0.1.0";
const INSTRUCTIONS_VIDEO_URL = "https://youtu.be/PO97DViA2Ec";
const BRIDGE_SIZE = "9.5 MB";

const CORAL = "#e2574c";
const CORAL_DARK = "#c8483d";
const NAVY = "#1f2a44";
const AMBER = "#e3963c";
const AMBER_DARK = "#8a5300";
const GREEN = "#2e7d32";
const GREEN_DARK = "#1b5e20";
const BLUE = "#3b5bdb";
const BLUE_DARK = "#2f4bc0";
const NEUTRAL_CARD_BG = "#f4f3ee";

const HELD_BACK_REASONS: Record<string, string> = {
  missing_party_name: "No party name found on this invoice -- Tally needs a ledger name to post against.",
  missing_invoice_number: "Invoice number is missing -- Tally requires a voucher number to import.",
  missing_or_unrecognized_invoice_date:
    "Invoice date is missing or in an unrecognized format -- held back for review instead of guessing.",
};

type Severity = "success" | "warning" | "error";

const OUTCOME_STYLES: Record<Severity, { bg: string; accent: string; titleColor: string; icon: ReactNode }> = {
  success: { bg: "#eaf7ee", accent: GREEN, titleColor: GREEN_DARK, icon: <CheckCircleIcon sx={{ color: "#fff", fontSize: 20 }} /> },
  warning: { bg: "#fff4e5", accent: AMBER, titleColor: AMBER_DARK, icon: <WarningAmberIcon sx={{ color: "#fff", fontSize: 20 }} /> },
  error: { bg: "#fdecea", accent: CORAL, titleColor: CORAL_DARK, icon: <CloseIcon sx={{ color: "#fff", fontSize: 20 }} /> },
};

interface Outcome {
  severity: Severity;
  title: string;
  subtitle: string;
}

interface HeldBackInvoice {
  invoiceNo: string;
  reason: string;
}

interface TallyErrorItem {
  category: string;
  invoiceNo?: string;
  message: string;
}

function StatCard({ icon, label, value, accent }: { icon: ReactNode; label: string; value: number; accent: string }) {
  return (
    <Box sx={{ flex: "1 1 45%", minWidth: 140, border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={800}>
        {value}
      </Typography>
    </Box>
  );
}

function SyncToTallyDialog({ open, setOpen }: IProps) {
  const [bridgeState, setBridgeState] = useState<BridgeState>("checking");
  const [token, setToken] = useState(getStoredBridgeToken());
  const [billingEntityId, setBillingEntityId] = useState<number | "">("");
  const [companyName, setCompanyName] = useState("");
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [stats, setStats] = useState({ ledgersCreated: 0, ledgersAltered: 0, invoicesCreated: 0, invoicesAltered: 0 });
  const [heldBack, setHeldBack] = useState<HeldBackInvoice[]>([]);
  const [tallyErrors, setTallyErrors] = useState<TallyErrorItem[]>([]);
  const [heldBackOpen, setHeldBackOpen] = useState(true);
  const [errorsOpen, setErrorsOpen] = useState(true);
  const [checklistOpen, setChecklistOpen] = useState(true);

  const checkBridge = () => {
    setBridgeState("checking");
    getCompanionStatus()
      .then(() => setBridgeState("connected"))
      .catch(() => setBridgeState("not_found"));
  };

  useEffect(() => {
    if (open) checkBridge();
  }, [open]);

  const { data: entities, isLoading: entitiesLoading }: ResType = useQuery(
    ["tally-billing-entities"],
    getTallyBillingEntities,
    { enabled: open, onError: (err: any) => snack.error(handleError(err)) }
  );

  const selectedEntity = entities?.data?.find((e: any) => e.id === billingEntityId);

  const { data: openCompaniesRes } = useQuery(
    ["tally-open-companies", token, bridgeState],
    () => getOpenCompanies(token),
    { enabled: open && bridgeState === "connected" && !!token, retry: false }
  );
  const openCompanies: string[] = openCompaniesRes?.ok ? openCompaniesRes.companies || [] : [];
  const matchesTally =
    !!companyName && openCompanies.some((c) => c.trim().toLowerCase() === companyName.trim().toLowerCase());

  const tips: { bold: string; text: string }[] = [];
  if (bridgeState !== "connected") {
    tips.push({
      bold: "Connect ATOM Tally Bridge.",
      text: "Install it on this PC, open it, and wait for Connected before you sync.",
    });
  }
  if (bridgeState === "connected" && !token) {
    tips.push({
      bold: "Paste the pairing line.",
      text: "Tally Bridge shows it when it opens -- paste it below and it connects automatically.",
    });
  }
  if (!billingEntityId) {
    tips.push({
      bold: "Pick a billing entity.",
      text: "Only entities with invoices ready to sync are selectable below.",
    });
  }
  if (billingEntityId) {
    tips.push({
      bold: "Names must match exactly.",
      text: "The Tally company name and the ATOM billing entity name have to be identical, or the bridge blocks the sync automatically rather than risk posting into the wrong client's books.",
    });
  }
  const pendingCount = tips.length;

  useEffect(() => {
    setOutcome(null);
    setSyncCompleted(false);
    setStats({ ledgersCreated: 0, ledgersAltered: 0, invoicesCreated: 0, invoicesAltered: 0 });
    setHeldBack([]);
    setTallyErrors([]);
    if (selectedEntity) setCompanyName(selectedEntity.tallyCompanyName || selectedEntity.name || "");
  }, [billingEntityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mutate: saveMapping } = useMutation(saveTallyCompanyMapping, {
    onError: (err: any) => snack.error(handleError(err)),
  });

  const { mutate: sync, isLoading: syncing } = useMutation(
    async () => {
      if (!billingEntityId) throw new Error("no_entity");
      if (!token) throw new Error("no_token");

      await getCompanionStatus().catch(() => {
        throw new Error("bridge_unreachable");
      });

      if (companyName !== selectedEntity?.tallyCompanyName) {
        saveMapping({ billingEntityId, tallyCompanyName: companyName });
      }

      const invoicesRes: any = await getTallySyncableInvoices({ queryKey: ["tally-sync-invoices", billingEntityId] } as any);
      const invoices = invoicesRes?.data?.invoices || [];
      if (!invoices.length) throw new Error("no_invoices");

      return pushInvoicesToTally(token, companyName, invoices);
    },
    {
      onSuccess: (data: any) => {
        if (data.error === "tally_unreachable") {
          setOutcome({
            severity: "error",
            title: "Couldn't reach TallyPrime",
            subtitle: data.message || "Could not reach TallyPrime on this machine.",
          });
          return;
        }
        if (data.company_check && data.company_check.matched === false) {
          setOutcome({ severity: "error", title: "Wrong company open in Tally", subtitle: data.company_check.message });
          return;
        }
        if (data.ok) {
          const ledgers = data.result?.ledgers || {};
          const invoices = data.result?.invoices || {};
          setStats({
            ledgersCreated: ledgers.created ?? 0,
            ledgersAltered: ledgers.altered ?? 0,
            invoicesCreated: invoices.created ?? 0,
            invoicesAltered: invoices.altered ?? 0,
          });

          const heldBackList: HeldBackInvoice[] = (data.skipped || [])
            .filter((s: any) => s.reason !== "already_in_tally")
            .map((s: any) => ({
              invoiceNo: s.invoiceNo || "(no invoice number)",
              reason: HELD_BACK_REASONS[s.reason] || s.reason,
            }));
          setHeldBack(heldBackList);

          const errorList: TallyErrorItem[] = (data.errors || []).map((e: any) => ({
            category: (e.category || "invoices").toUpperCase(),
            invoiceNo: e.invoiceNo,
            message: e.message,
          }));
          setTallyErrors(errorList);
          setHeldBackOpen(true);
          setErrorsOpen(true);
          setSyncCompleted(true);

          if (errorList.length > 0) {
            setOutcome({
              severity: "error",
              title: `Synced, with ${errorList.length} error${errorList.length > 1 ? "s" : ""} to fix`,
              subtitle: `Most of this batch posted; ${
                errorList.length > 1 ? "a few issues need" : "one issue needs"
              } attention in Tally -- see below.`,
            });
          } else if (heldBackList.length > 0) {
            setOutcome({
              severity: "warning",
              title: `Synced, with ${heldBackList.length} invoice${heldBackList.length > 1 ? "s" : ""} held back`,
              subtitle: `Everything else posted cleanly. Review the held-back invoice${
                heldBackList.length > 1 ? "s" : ""
              } below and resync once fixed.`,
            });
          } else {
            setOutcome({
              severity: "success",
              title: "Synced successfully",
              subtitle: "All ready invoices posted to TallyPrime cleanly.",
            });
          }
          return;
        }
        setOutcome({ severity: "error", title: "Unexpected response from the bridge", subtitle: "Try again, or check the bridge console for details." });
      },
      onError: (err: any) => {
        const map: Record<string, string> = {
          no_entity: "Pick a billing entity first.",
          no_token: "Paste the pairing line from Tally Bridge first.",
          bridge_unreachable: "Bridge not reachable. Is Tally Bridge running on this machine?",
          no_invoices: "No invoices ready to sync for this billing entity.",
          bridge_unauthorized: "Bridge rejected the pairing line -- re-paste it from Tally Bridge.",
        };
        setOutcome({ severity: "error", title: "Sync didn't run", subtitle: map[err?.message] || handleError(err) });
      },
    }
  );

  const handleTokenBlur = () => {
    setStoredBridgeToken(token);
    if (token) checkBridge();
  };

  return (
    <DialogWrapper width="sm" title="Sync to Tally" open={open} setOpen={setOpen}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Push ready invoices straight into TallyPrime through the local desktop bridge.
        </Typography>

        {/* One-time Tally setup note */}
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#fff6e9",
            border: "1px solid #f3d3a4",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningAmberIcon sx={{ color: AMBER, fontSize: 18 }} />
              <Typography variant="body2" fontWeight={700} sx={{ color: AMBER_DARK }}>
                Note
              </Typography>
            </Box>
            <Tooltip title="Watch setup instructions">
              <Box
                component="a"
                href={INSTRUCTIONS_VIDEO_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "flex", alignItems: "center", gap: 0.3, textDecoration: "none", color: "#ff0000" }}
              >
                <YouTubeIcon sx={{ fontSize: 20 }} />
                <Typography variant="caption" fontWeight={700} sx={{ color: AMBER_DARK }}>
                  Instructions
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              In Tally, go to <b>Help (F1) → Settings → Connectivity → Client/Server Configuration</b>. Under
              "TallyPrime Acts as," select <b>Server</b>, save (<b>Ctrl+A</b>), and confirm <b>Yes</b> when
              prompted — Tally will restart.
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              Once step 1 is complete, go to <b>Gateway of Tally → Alter → Voucher Type → Sales → Method of Voucher
              Numbering</b>, and set it to <b>Automatic (Manual Override)</b>.
            </Typography>
          </Box>
        </Box>

        {/* Bridge status card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: bridgeState === "not_found" ? "#fdecea" : NEUTRAL_CARD_BG,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: bridgeState === "not_found" ? CORAL : NAVY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SyncIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={800}>
                {bridgeState === "checking" && "Checking for ATOM Tally Bridge..."}
                {bridgeState === "connected" && "ATOM Tally Bridge connected"}
                {bridgeState === "not_found" && "ATOM Tally Bridge not found"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bridgeState === "not_found"
                  ? "Install and open it on this PC, then Recheck."
                  : `${BRIDGE_VERSION} running on this computer`}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
            <Chip
              size="small"
              label={bridgeState === "checking" ? "Checking..." : bridgeState === "connected" ? "● Connected" : "Not found"}
              sx={{
                fontWeight: 700,
                bgcolor: bridgeState === "connected" ? "#e6f4ea" : bridgeState === "not_found" ? "#fbdbd7" : "#eeeeee",
                color: bridgeState === "connected" ? GREEN_DARK : bridgeState === "not_found" ? CORAL_DARK : "text.secondary",
              }}
            />
            <Typography
              variant="caption"
              onClick={checkBridge}
              sx={{ color: "#182f53", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 0.3, whiteSpace: "nowrap" }}
            >
              <RefreshIcon sx={{ fontSize: 14 }} /> Recheck
            </Typography>
          </Stack>
        </Box>

        {/* Download card */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: "#182f53",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <DownloadIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={800}>ATOM Tally Bridge</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Setting up another computer?
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {BRIDGE_VERSION} · {BRIDGE_SIZE} · Windows only
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            href={CONSOLE_DOWNLOAD_URL}
            target="_blank"
            rel="noopener"
            sx={{ whiteSpace: "nowrap", flexShrink: 0, bgcolor: "#182f53"}}
          >
            Download
          </Button>
        </Box>

        {/* Before-you-sync checklist */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: pendingCount > 0 ? "#c9d7fb" : "#bfe3c8",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setChecklistOpen(!checklistOpen)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.5,
              py: 1,
              bgcolor: pendingCount > 0 ? "#eaf1fd" : "#eaf7ee",
              cursor: "pointer",
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <InfoOutlinedIcon sx={{ color: pendingCount > 0 ? BLUE : GREEN_DARK, fontSize: 18 }} />
              <Typography variant="body2" fontWeight={700} sx={{ color: pendingCount > 0 ? BLUE : GREEN_DARK }}>
                {pendingCount > 0
                  ? `Before you sync -- ${pendingCount} thing${pendingCount > 1 ? "s" : ""} to check`
                  : "You're all set to sync"}
              </Typography>
            </Box>
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: checklistOpen ? "rotate(180deg)" : "rotate(-90deg)",
                transition: "transform 0.15s",
                color: pendingCount > 0 ? BLUE : GREEN_DARK,
              }}
            />
          </Box>
          <Collapse in={checklistOpen}>
            <Stack divider={<Divider />}>
              {tips.map((tip, i) => (
                <Box key={i} sx={{ px: 1.5, py: 1, display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <ErrorOutlineIcon sx={{ color: BLUE, fontSize: 18, mt: "1px", flexShrink: 0 }} />
                  <Typography variant="body2" component="div">
                    <Typography component="span" variant="body2" fontWeight={700} color="text.primary">
                      {tip.bold}{" "}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary">
                      {tip.text}
                    </Typography>
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>

        <TextField
          label="Pairing line"
          size="small"
          fullWidth
          value={token}
          onChange={(e) => setToken(e.target.value)}
          onBlur={handleTokenBlur}
          placeholder="Paste the line Tally Bridge shows you when it opens"
          helperText="Paste the line Tally Bridge shows you when it opens -- it connects automatically."
        />

        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
            BILLING ENTITY
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <Select
              value={billingEntityId}
              onChange={(e) => setBillingEntityId(e.target.value as number)}
              disabled={entitiesLoading}
              displayEmpty
              renderValue={(val) => {
                if (!val) return <Typography color="text.secondary">Select a billing entity</Typography>;
                const e = entities?.data?.find((x: any) => x.id === val);
                return e ? `${e.name} (${e.syncableInvoiceCount} ready to sync)` : String(val);
              }}
            >
              {entities?.data?.map((e: any) => (
                <MenuItem key={e.id} value={e.id} disabled={!e.syncableInvoiceCount}>
                  <BillingEntityOption name={`${e.name} (${e.syncableInvoiceCount} ready to sync)`} hasGst={e.hasGst} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedEntity && (
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
              TALLY COMPANY
            </Typography>
            {matchesTally ? (
              <Box
                sx={{
                  mt: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1,
                  bgcolor: NEUTRAL_CARD_BG,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                }}
              >
                <Typography fontWeight={700}>{companyName}</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: GREEN_DARK, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.3 }}
                >
                  <CheckIcon sx={{ fontSize: 14 }} /> Matches Tally
                </Typography>
              </Box>
            ) : (
              <TextField
                variant="standard"
                fullWidth
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                InputProps={{ sx: { fontWeight: 700, fontSize: 16 } }}
                sx={{ mt: 0.5 }}
              />
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {matchesTally
                ? "This matches a company currently open in TallyPrime."
                : "Must match the company name exactly as it appears open in TallyPrime."}
            </Typography>
          </Box>
        )}

        <Button
          fullWidth
          disabled={syncing || bridgeState !== "connected" || !billingEntityId || !companyName}
          onClick={() => sync()}
          sx={{
            py: 1.5,
            fontWeight: 700,
            fontSize: 16,
            borderRadius: 1.5,
            bgcolor: CORAL,
            color: "#fff",
            "&:hover": { bgcolor: CORAL_DARK },
            "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
          }}
        >
          {syncing ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sync To Tally"}
        </Button>

        {outcome && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: OUTCOME_STYLES[outcome.severity].bg,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 1.5,
                bgcolor: OUTCOME_STYLES[outcome.severity].accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {OUTCOME_STYLES[outcome.severity].icon}
            </Box>
            <Box flex={1}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ color: OUTCOME_STYLES[outcome.severity].titleColor }}>
                {outcome.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {outcome.subtitle}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              Just now
            </Typography>
          </Box>
        )}

        {syncCompleted && (
          <Box display="flex" flexWrap="wrap" gap={1.5}>
            <StatCard icon={<MenuBookIcon sx={{ color: "#fff", fontSize: 18 }} />} label="LEDGERS CREATED" value={stats.ledgersCreated} accent={NAVY} />
            <StatCard icon={<MenuBookIcon sx={{ color: "#fff", fontSize: 18 }} />} label="LEDGERS ALTERED" value={stats.ledgersAltered} accent={NAVY} />
            <StatCard icon={<ReceiptLongIcon sx={{ color: "#fff", fontSize: 18 }} />} label="INVOICES CREATED" value={stats.invoicesCreated} accent={AMBER} />
            <StatCard icon={<ReceiptLongIcon sx={{ color: "#fff", fontSize: 18 }} />} label="INVOICES ALTERED" value={stats.invoicesAltered} accent={AMBER} />
          </Box>
        )}

        {heldBack.length > 0 && (
          <Box sx={{ border: "1px solid", borderColor: "#f5d9b0", borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setHeldBackOpen(!heldBackOpen)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, bgcolor: "#fff4e5", cursor: "pointer" }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <WarningAmberIcon sx={{ color: AMBER_DARK, fontSize: 18 }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: AMBER_DARK }}>
                  {heldBack.length} invoice{heldBack.length > 1 ? "s" : ""} held back before syncing
                </Typography>
              </Box>
              <ExpandMoreIcon
                fontSize="small"
                sx={{ transform: heldBackOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", color: AMBER_DARK }}
              />
            </Box>
            <Collapse in={heldBackOpen}>
              <Stack divider={<Divider />}>
                {heldBack.map((h, i) => (
                  <Box key={i} sx={{ px: 1.5, py: 1 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {h.invoiceNo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.reason}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {tallyErrors.length > 0 && (
          <Box sx={{ border: "1px solid", borderColor: "#f6cdc8", borderRadius: 2, overflow: "hidden" }}>
            <Box
              onClick={() => setErrorsOpen(!errorsOpen)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1, bgcolor: "#fdecea", cursor: "pointer" }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CloseIcon sx={{ color: CORAL_DARK, fontSize: 18 }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: CORAL_DARK }}>
                  {tallyErrors.length} error{tallyErrors.length > 1 ? "s" : ""} reported by Tally
                </Typography>
              </Box>
              <ExpandMoreIcon
                fontSize="small"
                sx={{ transform: errorsOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s", color: CORAL_DARK }}
              />
            </Box>
            <Collapse in={errorsOpen}>
              <Stack divider={<Divider />}>
                {tallyErrors.map((e, i) => (
                  <Box key={i} sx={{ px: 1.5, py: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
                      {e.category}
                      {e.invoiceNo ? ` · ${e.invoiceNo}` : ""}
                    </Typography>
                    <Typography variant="body2">{e.message}</Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}
      </Stack>
    </DialogWrapper>
  );
}

export default SyncToTallyDialog;
